/**
 * Decorative hero wheel — pure SVG, animated entirely in CSS.
 *
 * Not connected to entries state, and deliberately so: it is a picture of what
 * the product does, shown above the real wheel. Wiring it to live data would
 * make the page render two competing wheels and give a first-time visitor two
 * things to look at before they have used either.
 *
 * No canvas and no animation library. The whole thing is one rotating <g> plus
 * a static pointer, which means it costs nothing on the main thread, works in
 * the pre-rendered HTML with no JS at all, and honours prefers-reduced-motion
 * through a plain media query rather than a runtime check.
 *
 * Timing lives in index.css (`hero-wheel-spin`, `hero-wheel-flash`) so the
 * rotation and the winner pulse share one 5s clock and cannot drift.
 */

/** Decorative only — never shown to a screen reader, so real names are fine. */
const LABELS = ['Alice', 'Ben', 'Carlos', 'Diana', 'Eva', 'Frank', 'Grace', 'Hiro'];

/**
 * Eight tints of the brand violet (#7c3aed), alternating lightness so adjacent
 * segments separate without introducing a second hue. Kept as explicit hex
 * rather than generated, so what ships is exactly what was eyeballed.
 */
const SEGMENT_FILLS = [
  '#7c3aed', '#9f6bf3', '#6d28d9', '#b493f7',
  '#8b5cf6', '#5b21b6', '#a78bfa', '#4c1d95',
];

/** The segment the pointer rests on, which is also the one that pulses. */
const WINNER_INDEX = 0;

const SIZE = 240;
const R = SIZE / 2 - 6;
const CX = SIZE / 2;
const CY = SIZE / 2;
const SEGMENTS = LABELS.length;
const STEP = (2 * Math.PI) / SEGMENTS;

/** Pie slice path for segment `i`, starting at 12 o'clock. */
function slicePath(i: number): string {
  const a0 = i * STEP - Math.PI / 2;
  const a1 = a0 + STEP;
  const x0 = CX + R * Math.cos(a0);
  const y0 = CY + R * Math.sin(a0);
  const x1 = CX + R * Math.cos(a1);
  const y1 = CY + R * Math.sin(a1);
  return `M ${CX} ${CY} L ${x0.toFixed(2)} ${y0.toFixed(2)} A ${R} ${R} 0 0 1 ${x1.toFixed(2)} ${y1.toFixed(2)} Z`;
}

/** Where a label sits, and how far it is rotated to run along its slice. */
function labelTransform(i: number): { x: number; y: number; rotate: number } {
  const mid = i * STEP + STEP / 2 - Math.PI / 2;
  const rad = R * 0.62;
  return {
    x: CX + rad * Math.cos(mid),
    y: CY + rad * Math.sin(mid),
    rotate: (mid * 180) / Math.PI,
  };
}

export default function HeroWheelAnimation({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      // Purely presentational: it conveys nothing a caption does not, and
      // announcing eight invented names would be noise in a screen reader.
      aria-hidden="true"
      role="presentation"
      focusable="false"
      // 40% smaller below 640px, and the parent centres it above the headline.
      className={`w-[144px] h-[144px] sm:w-[240px] sm:h-[240px] ${className}`}
    >
      <defs>
        <filter id="hero-wheel-shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="3" stdDeviation="6" floodColor="#4c1d95" floodOpacity="0.28" />
        </filter>
      </defs>

      {/* Everything that turns. The pointer below sits outside this group so it
          stays fixed while the wheel rotates beneath it. */}
      <g className="animate-hero-wheel" filter="url(#hero-wheel-shadow)">
        {LABELS.map((label, i) => {
          const t = labelTransform(i);
          return (
            <g key={label}>
              <path d={slicePath(i)} fill={SEGMENT_FILLS[i]} stroke="#ffffff" strokeWidth="1.5" />
              <text
                x={t.x}
                y={t.y}
                transform={`rotate(${t.rotate} ${t.x} ${t.y})`}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#ffffff"
                fontSize="11"
                fontWeight="700"
                fontFamily="'Inter Variable', Inter, system-ui, sans-serif"
              >
                {label}
              </text>
            </g>
          );
        })}

        {/* Pulses white over the resting segment once the spin settles. Inside
            the rotating group so it stays glued to its slice. */}
        <path
          d={slicePath(WINNER_INDEX)}
          fill="#ffffff"
          className="animate-hero-flash"
        />
      </g>

      {/* Hub and pointer — static, drawn last so they sit above the slices. */}
      <circle cx={CX} cy={CY} r={R * 0.15} fill="#ffffff" />
      <circle cx={CX} cy={CY} r={R * 0.055} fill="#ddd6fe" />
      {/*
        Tip INSIDE the rim, body outside — the same orientation the real wheel
        uses (tipX = CX + WHEEL_RADIUS - 6 in SpinWheel). It was inverted here:
        the tip sat above the rim and the base below it, so the arrow pointed
        away from the wheel and marked nothing. A pointer has to indicate the
        segment it rests on, which means pointing into it.
      */}
      {/* Fill/stroke are the resting segment's colours (WINNER_INDEX = 0). While
          the wheel turns, `.hero-pointer` (index.css) animates them to follow
          whichever segment is under the pointer, on the same 5s clock. */}
      <polygon
        points={`${CX},${CY - R + 8} ${CX - 11},${CY - R - 20} ${CX + 11},${CY - R - 20}`}
        fill={SEGMENT_FILLS[WINNER_INDEX]}
        stroke="#ffffff"
        strokeWidth="2"
        strokeLinejoin="round"
        className="hero-pointer"
      />
    </svg>
  );
}
